<template lang="pug">
	.lexicon--editor.flex-col.space-large.full-width
		Loader(v-if="isLoading", :text="$tCaption('Loading', '@actions') + '..'", :fullscreen="true")
		.flex-row.space.align-center
			.flex-col.flex-1(v-if="lexiconBO")
				.font-small.upper.grey {{$tCaption('Name')}}
				DxTextBox(:value.sync="lexiconBO.Name", valueChangeEvent="input")
			.flex-col.flex-2
				.font-tiny.upper.grey {{$tCaption('Alphabet')}}
				DxSelectBox(:value.sync="alphabet", :data-source="alphabets", display-expr="name", value-expr="id", :dropDownOptions="{ width: '320px' }")
			.flex-col.flex-2
				.font-tiny.upper.grey {{$tCaption('Language')}}
				DxSelectBox(:dataSource="getStore(languages)", keyExpr="locale", displayExpr="text", :searchEnabled="true", :value.sync="lang", placeholder="", :showClearButton="true")
		.flex-col.space
			.flex-row.space
				.upper.grey.flex-row.align-center.space
					span {{$tCaption('Lexemes')}}
					span(v-if="lexemes.length") ({{lexemes.length}})
				.adt-button.small.clickable.hoverlable.flex-row.space.align-center.center(@click="addAlias")
					fas(icon="plus" size="2xs")
					span {{$tCaption('Alias')}}
				.adt-button.small.clickable.hoverlable.flex-row.space.align-center.center(@click="addPhoneme")
					fas(icon="plus" size="2xs")
					span {{$tCaption('Phoneme')}}
			DxScrollView(showScrollbar="always", :height="scrollHeight || 260", width="100%", ref="scroll")
				.flex-col.space
					LexiconEditorItem(v-for="(lexeme, i) in lexemes", :key="lexeme.id", :lexeme.sync="lexeme.item", @remove="removeLexeme(lexeme, i)", @change="emitChange()")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import axios from 'axios';
	import LexiconEditorItem from './LexiconEditorItem.vue';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import { ALPHABETS, LEXICON_TYPES } from 'app-core/constants';
	import DxSelectBox from 'devextreme-vue/select-box';
	import cognitiveArrays from 'view/insertion/cognitive-arrays';
	import ArrayStore from 'devextreme/data/array_store';
	import { saveAs } from 'file-saver';

	export default {
		translationCategory: 'lexicon.LexiconEditor',
		name: 'lexicon--editor',
		props: ['value', 'url', 'scrollHeight', 'lexiconName', 'lexiconBO'],
		components: { LexiconEditorItem, DxTextBox, Loader, DxScrollView, DxSelectBox },
		mixins: [cognitiveArrays],
		data() {
			return {
				lexicon: null,
				lexemes: [],
				isLoading: false,
				alphabets: ALPHABETS.toLocale('name', this),
				lexiconTypes: LEXICON_TYPES.toLocale('name', this)
			};
		},
		computed: {
			alphabet: {
				get: function() { return this.lexicon?.getAttribute('alphabet') },
				set: function(v) { this.lexicon?.setAttribute('alphabet', v) },
			},
			lang: {
				get: function() { return this.lexicon?.getAttribute('xml:lang') },
				set: function(v) { this.lexicon?.setAttribute('xml:lang', v) },
			}
		},
		watch: {
			'url'() {
				this.loadLexicon(true);
			},
			'value'() {
				this.parseLexiconString(this.value);
			},
			'lexemes': {
				handler() {
					this.refreshScroll();
				},
				deep: true
			},
			lexicon: {
				handler() {
					this.emitChange();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.lexiconEditor = this;
			if (this.value) {
				this.parseLexiconString(this.value);
			} else {
				this.loadLexicon();
			}
		},
		methods: {
			download() {
				saveAs(new Blob([this.lexiconToString()], { type: "text/xml;charset=utf-8" }), `${this.lexiconName || 'lexicon'}.xml`);
			},
			getStore(items) {
				return new ArrayStore({
					data: items,
					key: 'locale'
				});
			},
			refreshScroll() {
				this.$nextTick(() => {
					this.$refs.scroll.instance.scrollBy(0.000001);
				});
			},
			calcLexemes() {
				var items = this.lexicon?.querySelectorAll('lexeme') || [];
				var result = [];
				for(var i = 0; i < items.length;i++) {
					result.push(this.getLexemeItem(items[i]));
				}
				this.lexemes = result;
			},
			removeLexeme(lexeme, idx) {
				lexeme.item.remove();
				this.lexemes.splice(idx, 1);
				this.emitChange();
			},
			async loadLexicon(force) {
				if ((!this.lexicon || force) && this.url) {
					this.isLoading++;
					await axios.get(this.url + `?timestamp=${this.$Data.uuid()}`).then(r => {
						if (__DEBUG__)
							console.debug('Lexicon loaded', { r });
						this.parseLexicon(r?.data);
					}).finally(() => this.isLoading--);
				} else {
					if (!this.lexicon && !this.url) {
						this.parseLexicon(
							`<lexicon xmlns="http://www.w3.org/2005/01/pronunciation-lexicon" 
								xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.0" 
								xsi:schemaLocation="http://www.w3.org/2005/01/pronunciation-lexicon         http://www.w3.org/TR/2007/CR-pronunciation-lexicon-20071212/pls.xsd" 
								alphabet="ipa" xml:lang="en-US">
							</lexicon>`)
					}
				}
			},
			parseLexicon(str) {
				const parser = new DOMParser();
				this.lexicon = parser.parseFromString(str, 'application/xml')?.documentElement;
				this.calcLexemes();
			},
			lexiconToString() {
				if (!this.lexicon) return null;
				var xsltDoc = new DOMParser().parseFromString([
					// describes how we want to modify the XML - indent everything
					'<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
					'  <xsl:strip-space elements="*"/>',
					'  <xsl:template match="para[content-style][not(text())]">', // change to just text() to strip space in text nodes
					'    <xsl:value-of select="normalize-space(.)"/>',
					'  </xsl:template>',
					'  <xsl:template match="node()|@*">',
					'    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
					'  </xsl:template>',
					'  <xsl:output indent="yes"/>',
					'</xsl:stylesheet>',
				].join('\n'), 'application/xml');

				var xsltProcessor = new XSLTProcessor();    
				xsltProcessor.importStylesheet(xsltDoc);
				var resultDoc = xsltProcessor.transformToDocument(this.lexicon);
				var resultXml = new XMLSerializer().serializeToString(resultDoc);
				return resultXml.replace(/xmlns\=\"http:\/\/www.w3.org\/1999\/xhtml\"/g, '').replace(/ \>/g, '>');
			},
			getLexemeItem(lexeme) {
				return {
					id: this.$Data.uuid(),
					item: lexeme
				};
			},
			addAlias() {
				var newLexeme = document.createElement('lexeme');
				newLexeme.appendChild(document.createElement('grapheme'));
				newLexeme.appendChild(document.createElement('alias'));
				this.lexicon.appendChild(newLexeme);
				this.lexemes.push(this.getLexemeItem(newLexeme));
				this.emitChange();

				this.$nextTick(() => this.$refs.scroll.instance.scrollBy(9999));
			},
			addPhoneme(opts) {
				var newLexeme = document.createElement('lexeme');
				const grapheme = document.createElement('grapheme');
				const phoneme = document.createElement('phoneme');
				newLexeme.appendChild(grapheme);
				newLexeme.appendChild(phoneme);

				if (opts?.grapheme) {
					grapheme.innerHTML = opts?.grapheme;
				}

				if (opts?.phoneme) {
					phoneme.innerHTML = opts?.phoneme;
				}

				this.lexicon.appendChild(newLexeme);
				this.lexemes.push(this.getLexemeItem(newLexeme));
				this.emitChange();

				this.$nextTick(() => this.$refs.scroll.instance.scrollBy(9999));
			},
			emitChange() {
				if (__DEBUG__)
					console.debug('Lexicon editor change', { lexicon: this.lexicon });
				this.$emit('change', this.lexiconToString(), this.lexicon);
			}
		}
	};
</script>
<style lang='scss'>
	.lexicon--editor {
		
	}
</style>
